import { useCallback, useMemo } from 'react';

import { useModuleTabsInstructions } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/useModuleTabsInstructions';
import {
  EmployeeModuleConfig,
  EmployeeModuleTabProps,
  EmployeeModuleTabSetupProps,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/types';

type PropsType<TabPath, TabConfigKey, TabInstructionKey> = EmployeeModuleConfig<
  TabPath,
  TabConfigKey,
  TabInstructionKey
> & {
  modulePath: string;
  tabsSetupProps: EmployeeModuleTabSetupProps<TabPath>[];
  enabledTabPaths: TabPath[];
};

export const usePrepModuleTabProps = <TabPath, TabConfigKey, TabInstructionKey>(
  props: PropsType<TabPath, TabConfigKey, TabInstructionKey>,
) => {
  const { modulePath, tabsSetupProps, tabConfigs, enabledTabPaths } = props;

  // Tab Instructions
  const { isLoading: isLoadingInstructions, tabInstructions } =
    useModuleTabsInstructions({
      tabConfigs,
    });

  const getTabHelp = useCallback(
    (tab: TabPath | string | undefined): string | undefined => {
      if (typeof tab === 'undefined') {
        return undefined;
      }

      const id = tab as unknown as string;

      return Object.keys(tabInstructions).includes(id)
        ? tabInstructions[id]
        : undefined;
    },
    [tabInstructions],
  );

  const getPath = useCallback(
    (tab: TabPath) => `${modulePath}/${tab}`,
    [modulePath],
  );
  const getHref = useCallback(
    (tab: TabPath) => `/v2${getPath(tab)}`,
    [getPath],
  );

  const { tabs } = useMemo(() => {
    // TODO: Add checks and rules for resolving disabled tabs
    const disabledTabs: TabPath[] = [];

    const isTabDisabled = (tab: TabPath) => {
      return disabledTabs.includes(tab);
    };

    const tabs = tabsSetupProps
      .filter((t) => enabledTabPaths.includes(t.id))
      .map((t): EmployeeModuleTabProps<TabPath> => {
        return {
          ...t,
          href: getHref(t.id),
          active: false,
          disabled: isTabDisabled(t.id),
          tabId: t.id,
          tabHelp: getTabHelp(t.id),
        };
      });

    return {
      tabs,
    };
  }, [tabsSetupProps, getHref, getTabHelp, enabledTabPaths]);

  return {
    isLoading: isLoadingInstructions,
    tabs,
  };
};
